import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth, LoginResult } from './AuthContext';
import { API_BASE } from '../config/api';
import { formatAuthErrorMessage } from './authErrorUtils';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: any) => void;
            error_callback?: (error: any) => void;
            prompt?: string;
          }) => {
            requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
          };
        };
        id: {
          initialize: (config: any) => void;
          prompt: (notification?: any) => void;
          renderButton: (parent: HTMLElement, options: any) => void;
        };
      };
    };
  }
}

export function useGoogleAuth() {
  const { loginWithGoogle } = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [cachedClientId, setCachedClientId] = useState<string | null>(() => {
    const envId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID;
    return envId && envId.trim().length > 0 && !envId.includes('votre_client_id') ? envId.trim() : null;
  });

  const isFetchingConfig = useRef(false);

  // Charger le clientId depuis le backend si absent des variables Vite
  useEffect(() => {
    if (cachedClientId || isFetchingConfig.current) return;
    isFetchingConfig.current = true;

    fetch(`${API_BASE}/auth/google/config`)
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        if (data.success && data.clientId && data.clientId.trim().length > 0 && !data.clientId.includes('votre_client_id')) {
          setCachedClientId(data.clientId.trim());
        }
      })
      .catch(() => {})
      .finally(() => {
        isFetchingConfig.current = false;
      });
  }, [cachedClientId]);

  // Pré-charger le script Google Identity Services dès le montage pour que le SDK soit prêt au clic
  useEffect(() => {
    ensureGoogleScript().catch(() => {});
  }, []);

  // S'assurer que le script Google Identity Services est injecté et disponible
  const ensureGoogleScript = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;
    if (window.google?.accounts?.oauth2) return true;

    return new Promise((resolve) => {
      const checkSdkReady = () => Boolean(window.google?.accounts?.oauth2);

      if (checkSdkReady()) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (existingScript) {
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (checkSdkReady()) {
            clearInterval(interval);
            resolve(true);
          } else if (attempts > 30) {
            clearInterval(interval);
            // Si le script initial n'a pas répondu, tenter de réinjecter
            try { existingScript.remove(); } catch {}
            injectFreshScript();
          }
        }, 100);
        return;
      }

      injectFreshScript();

      function injectFreshScript() {
        if (checkSdkReady()) {
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;

        script.onload = () => {
          let attempts = 0;
          const interval = setInterval(() => {
            attempts++;
            if (checkSdkReady()) {
              clearInterval(interval);
              resolve(true);
            } else if (attempts > 30) {
              clearInterval(interval);
              resolve(checkSdkReady());
            }
          }, 100);
        };

        script.onerror = (err) => {
          console.error('[GoogleAuth] Impossible de charger le script Google Identity Services (bloqué par le navigateur ou réseau):', err);
          resolve(false);
        };

        document.head.appendChild(script);
      }
    });
  }, []);

  const clearGoogleError = useCallback(() => {
    setGoogleError(null);
  }, []);

  const triggerGoogleLogin = useCallback(async (): Promise<LoginResult | null> => {
    setGoogleError(null);

    // Résoudre le Client ID (depuis l'état, l'env ou un fetch synchrone)
    let clientId = cachedClientId;
    if (!clientId) {
      const envId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID;
      if (envId && envId.trim().length > 0 && !envId.includes('votre_client_id')) {
        clientId = envId.trim();
      }
    }

    if (!clientId) {
      try {
        const res = await fetch(`${API_BASE}/auth/google/config`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.clientId && data.clientId.trim().length > 0 && !data.clientId.includes('votre_client_id')) {
            clientId = data.clientId.trim();
            setCachedClientId(clientId);
          }
        }
      } catch {}
    }

    // Si aucun Google Client ID n'est configuré
    if (!clientId) {
      setShowConfigModal(true);
      return null;
    }

    setIsGoogleLoading(true);

    const scriptReady = await ensureGoogleScript();
    if (!scriptReady || !window.google?.accounts?.oauth2) {
      setIsGoogleLoading(false);
      setGoogleError("Impossible de charger les services Google. Vérifiez votre connexion internet ou désactivez votre bloqueur de publicités (AdBlock, Brave Shields) pour ce site.");
      return null;
    }

    return new Promise<LoginResult | null>((resolve) => {
      try {
        const client = window.google!.accounts.oauth2.initTokenClient({
          client_id: clientId!,
          scope: 'openid email profile',
          callback: async (response: any) => {
            if (response.error) {
              setIsGoogleLoading(false);
              const friendly = formatAuthErrorMessage(response.error_description || response.error, "La connexion Google a échoué.");
              if (friendly) {
                setGoogleError(friendly);
              }
              resolve(null);
              return;
            }

            if (response.access_token) {
              try {
                const result = await loginWithGoogle({ accessToken: response.access_token });
                resolve(result);
              } catch (err: any) {
                const friendly = formatAuthErrorMessage(err, "Erreur lors de l'authentification auprès du serveur.");
                if (friendly) setGoogleError(friendly);
                resolve(null);
              } finally {
                setIsGoogleLoading(false);
              }
            } else {
              setIsGoogleLoading(false);
              resolve(null);
            }
          },
          error_callback: (err: any) => {
            setIsGoogleLoading(false);
            const friendly = formatAuthErrorMessage(err?.message || err?.type || err?.error, "");
            if (friendly) {
              setGoogleError(friendly);
            }
            resolve(null);
          }
        });

        client.requestAccessToken({ prompt: 'select_account' });
      } catch (err: any) {
        setIsGoogleLoading(false);
        const friendly = formatAuthErrorMessage(err, "Impossible de démarrer l'authentification Google.");
        if (friendly) setGoogleError(friendly);
        resolve(null);
      }
    });
  }, [cachedClientId, ensureGoogleScript, loginWithGoogle]);

  return {
    triggerGoogleLogin,
    isGoogleLoading,
    googleError,
    clearGoogleError,
    showConfigModal,
    setShowConfigModal,
    isGoogleConfigured: Boolean(cachedClientId)
  };
}
